import { useState, type FormEvent } from "react";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/dahbd3roh653ivfvnqdg";

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [msgLength, setMsgLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("website_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMsgLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const strValue = typeof value === "string" ? value : value.name;
      if (strValue.trim() === "") return;
      payload.append(key, strValue);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMsgLength(0);
      } else {
        setStatus("error");
        setFormError(serverMsg && serverMsg.trim() ? serverMsg : "提交没成功，请稍后再试一次");
      }
    } catch {
      setStatus("error");
      setFormError("网络好像不太稳定，请检查后重试");
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 w-full bg-background-50">
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr]">
            <div className="p-6 md:p-9 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium w-fit">
                <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true"></span>
                订阅 · 联系
              </span>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl text-foreground-950 leading-tight">
                重要节点，直接推到你邮箱
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed max-w-md">
                留个邮箱，我们会在事件出现关键转折时做一次汇总推送，不会天天打扰。纠错、投稿、合作也可以直接从右边这张表发过来。
              </p>

              <ul className="mt-6 flex flex-col gap-3">
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="w-8 h-8 rounded-md bg-background-50 text-accent-700 flex items-center justify-center shrink-0">
                    <i className="ri-mail-send-line" aria-hidden="true"></i>
                  </span>
                  关键转折汇总推送，不发无关内容
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="w-8 h-8 rounded-md bg-background-50 text-accent-700 flex items-center justify-center shrink-0">
                    <i className="ri-shield-check-line" aria-hidden="true"></i>
                  </span>
                  邮箱只用于推送，不会用于其他用途
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="w-8 h-8 rounded-md bg-background-50 text-accent-700 flex items-center justify-center shrink-0">
                    <i className="ri-message-3-line" aria-hidden="true"></i>
                  </span>
                  留言通常 1 个工作日内回复
                </li>
              </ul>
            </div>

            <div className="p-6 md:p-9 bg-background-50 border-t lg:border-t-0 lg:border-l border-background-200">
              <h3 className="font-heading text-xl text-foreground-950">留下你的邮箱与需求</h3>
              <p className="mt-1.5 text-xs text-foreground-500">带 * 的为必填项</p>

              <form
                id="guatian-subscribe-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sub-name" className="text-sm text-foreground-800">
                    怎么称呼你
                  </label>
                  <input
                    id="sub-name"
                    name="name"
                    type="text"
                    required
                    placeholder="例如：阿哲"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sub-email" className="text-sm text-foreground-800">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="sub-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sub-interest" className="text-sm text-foreground-800">
                    你最关心哪个板块
                  </label>
                  <select
                    id="sub-interest"
                    name="interest"
                    defaultValue="全部板块"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
                  >
                    <option value="全部板块">全部板块</option>
                    <option value="娱乐动态">娱乐动态</option>
                    <option value="影视前线">影视前线</option>
                    <option value="科技热点">科技热点</option>
                    <option value="体育现场">体育现场</option>
                    <option value="城市生活">城市生活</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="sub-message" className="text-sm text-foreground-800">
                    想说的话 / 纠错线索
                  </label>
                  <textarea
                    id="sub-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setMsgLength(e.target.value.length)}
                    placeholder="有线索、发现错误，或者想聊聊，都可以写在这里（最多 500 字）"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  ></textarea>
                  <span className="text-xs text-foreground-400 text-right">{msgLength}/500</span>
                </div>

                <div className="form-guard-field" aria-hidden="true">
                  <label htmlFor="sub-website-alt">网站备用地址（请留空）</label>
                  <input
                    id="sub-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i
                    className={`${status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}`}
                    aria-hidden="true"
                  ></i>
                  {status === "submitting" ? "正在提交…" : "提交并订阅"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 text-sm text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-3.5 py-2.5">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    收到啦，快讯推送会发到你填写的邮箱。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}